import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { HcpGroup, Profile, ProfileType } from '@ixicare/resources';
import { Table, IconButton, Button } from '@ixicare/ui';
import React from 'react';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { HcpGroupCreateEditModal } from '~/containers/HcpGroups/HcpGroupCreateEditModal';

interface HcpGroupsTableProps {
  canCreate: boolean;
  canEdit: boolean;
  currentUserId?: number;
}

export function HcpGroupsTable({ canCreate, canEdit }: HcpGroupsTableProps) {
  const authenticatedUser = useStores().auth.user;
  const queryClient = useQueryClient();
  const { notifications } = useStores();
  const [editingGroup, setEditingGroup] = React.useState<{ id: string; name: string } | null>(null);

  const { data: groups, refetch } = useQuery({
    queryKey: ['hcpGroups'],
    queryFn: () => HcpGroup.getHcpGroups(),
  });

  // Fetch all HCPs to count them per group
  const { data: allHcps } = useQuery({
    queryKey: ['hcps'],
    queryFn: () => Profile.getProfiles({ profileType: ProfileType.HealthcareProvider }),
  });

  // Count HCPs per group
  const getHcpCount = (groupId: string) => {
    if (!allHcps) return 0;
    return allHcps.filter((hcp) => hcp.hcpGroupId === groupId).length;
  };

  // Get current user's HCP profile
  const currentUserHcp = React.useMemo(() => {
    if (!allHcps || !authenticatedUser?.id) return null;
    return allHcps.find((hcp) => hcp.id === authenticatedUser.id);
  }, [allHcps, authenticatedUser?.id]);

  // Check if user is in this specific group
  const isUserInGroup = (groupId: string) => {
    return currentUserHcp?.hcpGroupId === groupId;
  };

  // Mutation to join a group
  const joinGroupMutation = useMutation({
    mutationFn: ({ groupId, hcpId }: { groupId: string; hcpId: number }) =>
      HcpGroup.assignHcpToHcpGroup(groupId, hcpId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      queryClient.invalidateQueries({ queryKey: ['hcps'] });
      notifications.addUserActionNotification({
        type: 'success' as any,
        notificationType: 'success',
        message: t('hcpGroups.assign.success'),
      });
    },
    onError: (error) => {
      console.error('Error joining group:', error);
    },
  });

  const handleJoinGroup = (groupId: string) => {
    if (authenticatedUser) {
      joinGroupMutation.mutate({ groupId, hcpId: authenticatedUser.id });
    }
  };

  const handleEdit = (groupId: string) => {
    const group = groups?.find((g) => g.id === groupId);
    if (group) {
      setEditingGroup({ id: groupId, name: group.hcpGroupName });
    }
  };

  const { confirmation } = useStores();

  const handleDelete = (group: any) => {
    const hcpCount = getHcpCount(group.id);

    // Prevent deletion if group has HCPs assigned
    if (hcpCount > 0) {
      notifications.addUserActionNotification({
        type: 'error' as any,
        notificationType: 'error',
        message: t('hcpGroups.delete.hasHcps', { count: hcpCount }),
      });
      return;
    }

    confirmation.show({
      title: t('hcpGroups.delete.confirmTitle'),
      message: t('hcpGroups.delete.confirmMessage'),
      onConfirm: () => {
        HcpGroup.deleteHcpGroup(group.id)
          .then(() => {
            refetch();
            notifications.addUserActionNotification({
              type: 'success' as any,
              notificationType: 'success',
              message: t('hcpGroups.delete.success'),
            });
          })
          .catch((e) => {
            console.error(e);
            notifications.addUserActionNotification({
              type: 'error' as any,
              notificationType: 'error',
              message: t('hcpGroups.delete.error'),
            });
            refetch();
          });
      },
      onCancel: () => {},
    });
  };

  const columns = React.useMemo(() => {
    const cols = [
      t('hcpGroups.table.groupName'),
      t('hcpGroups.table.numberOfHcps'),
      t('hcpGroups.table.numberOfZones'),
      t('hcpGroups.table.membership'),
    ];

    // Add edit column for admin users
    if (canCreate || canEdit) {
      cols.push(t('profiles.table.edit'));
    }

    return cols;
  }, [canCreate, canEdit]);

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            {columns.map((text, i) => (
              <Table.HeadCol key={i}>
                <span>{text}</span>
              </Table.HeadCol>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {Array.isArray(groups) &&
            groups.map((group) => (
              <Table.Row key={`hcp-group-${group.id}`}>
                <Table.Col>{group.hcpGroupName}</Table.Col>
                <Table.Col>{getHcpCount(group.id) || '0'}</Table.Col>
                <Table.Col>{group.zones?.length || '0'}</Table.Col>
                <Table.Col>
                  {isUserInGroup(group.id) ? (
                    <span style={{ fontWeight: 500 }}>{t('hcpGroups.currentGroup')}</span>
                  ) : (
                    <Button onClick={() => handleJoinGroup(group.id)}>{t('hcpGroups.join.button')}</Button>
                  )}
                </Table.Col>

                {/* Edit/Delete column for admin users */}
                {(canCreate || canEdit) && (
                  <Table.Col>
                    {canEdit ? (
                      <IconButton icon="edit" onClick={() => handleEdit(group.id)} />
                    ) : canCreate ? (
                      <IconButton icon="lock" />
                    ) : null}
                    {canEdit ? (
                      <IconButton icon="delete" onClick={() => handleDelete(group)} />
                    ) : canCreate ? (
                      <IconButton icon="lock" />
                    ) : null}
                  </Table.Col>
                )}
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
      {editingGroup && (
        <HcpGroupCreateEditModal
          onClose={() => setEditingGroup(null)}
          groupId={editingGroup.id}
          groupName={editingGroup.name}
        />
      )}
    </>
  );
}
